import { ORIGIN } from "../utils/configs";

const PROTOCOL = "cryptember.commands.v1";
const TERMINAL = new Set(["ACTION_REQUIRED", "ACCEPTED", "REJECTED", "EXPIRED", "CANCELLED", "OUTCOME_UNKNOWN", "NOT_FOUND"]);
const clients = new Map();
let session = null;
let generation = 0;
const STORAGE_PREFIX = "cryptember-command-ids-v1:";
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const slowStages = new Map();
function recordSlowStage(stage, startedAt) {
  const now = performance.now();
  const elapsedMs = Math.round(now - startedAt);
  if (elapsedMs < 500 || (slowStages.has(stage) && now - slowStages.get(stage) < 10_000)) return;
  slowStages.set(stage, now);
  // Fixed stage names and durations only; no command IDs, payloads or authorization data.
  console.info(`[Insights command timing] ${stage}: ${elapsedMs} ms`);
}
function readIds(key) {
  if (!key) return [];
  try { const ids = JSON.parse(sessionStorage.getItem(key) || "[]"); return Array.isArray(ids) ? ids.filter(id => typeof id === "string" && UUID.test(id)).slice(-16) : []; } catch (_) { return []; }
}
function rememberId(key, id, remove = false) {
  if (!key) return;
  try {
    const ids = readIds(key).filter(value => value !== id);
    if (!remove) ids.push(id);
    if (ids.length) sessionStorage.setItem(key, JSON.stringify(ids.slice(-16))); else sessionStorage.removeItem(key);
  } catch (_) { /* Restricted storage does not queue or resend a command. */ }
}

export class CommandTransportError extends Error {
  constructor(code, message, commandId, status = 503) {
    super(message); this.name = "CommandTransportError"; this.code = code; this.commandId = commandId; this.status = status;
  }
}

export function configureCommandSession(value) {
  const changed = session?.sessionId !== value?.sessionId || session?.publicUserId !== value?.publicUserId || session?.csrfToken !== value?.csrfToken;
  if (changed) {
    generation += 1;
    clients.forEach(client => client.close()); clients.clear();
    if (session?.sessionId) ["USER", "ADMIN"].forEach(scope => { try { sessionStorage.removeItem(`${STORAGE_PREFIX}${session.sessionId}:${scope}`); } catch (_) {} });
  }
  session = value?.csrfToken ? value : null;
  if (changed && session?.sessionId) recoverCommandStatuses("USER");
}

export function closeAdminCommandTransport() {
  clients.get("ADMIN")?.close(); clients.delete("ADMIN");
}

export async function cancelJavaCommand(commandId, scope = "USER") {
  if (!UUID.test(commandId || "") || !["USER", "ADMIN"].includes(scope)) throw new CommandTransportError("INVALID_COMMAND", "The original command could not be identified.");
  const client = clients.get(scope);
  if (!client?.pending.has(commandId)) throw new CommandTransportError("STATUS_REQUIRED", "The command is no longer pending in this tab. Check its activity before taking another action.");
  await client.connect(client.endpoint);
  if (client.closed || client.socket?.readyState !== WebSocket.OPEN) throw new CommandTransportError("COMMAND_OFFLINE", "Cancellation could not be sent. Check the command outcome when App backend reconnects.");
  client.socket.send(JSON.stringify({ protocol: PROTOCOL, kind: "cancel", commandId }));
}

export async function recoverCommandStatuses(scope = "USER") {
  const recoveryGeneration = generation;
  if (!session?.sessionId) return;
  const ids = readIds(`${STORAGE_PREFIX}${session.sessionId}:${scope}`);
  // Keep the ordinary user's socket ready even when there are no receipts to recover.
  // Administrator sockets still require an explicitly initiated elevated operation.
  if (!ids.length && scope !== "USER") return;
  try {
    const transport = await commandTransportMode();
    if (transport.mode !== "secure" || generation !== recoveryGeneration) return;
    if (!clients.has(scope)) clients.set(scope, new CommandSocket(scope));
    const client = clients.get(scope);
    const onRecovered = response => {
      if (generation === recoveryGeneration) window.dispatchEvent(new CustomEvent("cryptember-command-result", { detail: {
        commandId: response.commandId, scope, state: response.state, code: response.code, accepted: response.accepted, message: commandResultMessage(response), recovered: true,
      } }));
    };
    for (const id of ids) {
      if (generation !== recoveryGeneration || client.pending.has(id)) continue;
      client.query(id).then(onRecovered);
    }
    // Queue status-only recovery before connecting, so an offline Java host still
    // leaves pending IDs for bounded reconnect. Never retain or resend payloads.
    client.endpoint = transport.endpoint;
    await client.connect(transport.endpoint).catch(() => client.scheduleRecovery());
  } catch (_) { /* Keep original IDs for a later authenticated status check. */ }
}

async function request(path, body) {
  const requestGeneration = generation;
  const response = await fetch(`${ORIGIN}${path}`, {
    method: body === undefined ? "GET" : "POST", credentials: "same-origin", cache: "no-store",
    headers: { Accept: "application/json", ...(body === undefined ? {} : { "Content-Type": "application/json", "X-CSRF-Token": session?.csrfToken || "" }) },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  const result = await response.json();
  if (response.status === 401 && requestGeneration === generation) window.dispatchEvent(new Event("cryptember-auth-expired"));
  if (!response.ok) throw new CommandTransportError(result.error || "COMMAND_SERVICE_UNAVAILABLE",
    response.status === 401 ? "Your session expired. Sign in again."
      : response.status === 403 && body?.scope === "ADMIN" ? "Administrator elevation is required. Open Profile → Admin and sign in before sending this command."
      : "The command service is unavailable. No new command was sent.", null, response.status);
  return result;
}

export async function commandTransportMode() {
  const startedAt = performance.now();
  const value = await request("/insights/account/command-transport").finally(() => recordSlowStage("MODE_LOOKUP", startedAt));
  if (!["legacy", "draining", "secure"].includes(value.mode)) throw new CommandTransportError("INVALID_TRANSPORT", "The command transport could not be verified. Refresh the page.");
  return value;
}

function uncertain(commandId) {
  return { protocol: PROTOCOL, kind: "response", commandId, state: "OUTCOME_UNKNOWN", accepted: false,
    code: "OUTCOME_UNKNOWN", message: "The command outcome is unknown. Check your activity and positions before submitting another command." };
}

export function commandResultMessage(result) {
  const ack = result?.acknowledgement || result;
  if (ack?.state === "ACTION_REQUIRED") return ack.code === "TRADING_LOCKED"
    ? "Trading is locked. Enter your Cryptember password in Telegram to continue."
    : "Continue the pending confirmation in Telegram. The original command deadline still applies.";
  if (ack?.code === "IDENTITY_RESULT_EXPIRED") return "The one-time identity result expired. Load the identity again.";
  if (["OUTCOME_UNKNOWN", "NOT_FOUND"].includes(ack?.state)) return uncertain(ack.commandId).message;
  if (ack?.state === "ACCEPTED" && ack?.code === "COMMAND_ACCEPTED") return "Command accepted by App backend. Check activity for the exchange outcome.";
  return ack?.message?.replace(/\bJava\b/g, "App backend") || (ack?.accepted ? "Applied by App backend." : "The command has not been confirmed.");
}

function envelope(response) {
  return { status: "ACKNOWLEDGED", commandId: response.commandId, acknowledgement: { ...response, message: commandResultMessage(response) } };
}

class CommandSocket {
  constructor(scope) {
    this.scope = scope; this.pending = new Map(); this.socket = null; this.connecting = null; this.closed = false; this.retry = null; this.attempts = 0;
    this.storageKey = session?.sessionId ? `${STORAGE_PREFIX}${session.sessionId}:${scope}` : null;
  }
  close() {
    this.closed = true; clearTimeout(this.retry); clearInterval(this.heartbeat);
    this.pending.forEach(entry => { clearTimeout(entry.timer); entry.resolve(uncertain(entry.id)); }); this.pending.clear();
    this.socket?.close(1000, "Session changed");
  }
  async connect(endpoint) {
    if (this.closed) throw new CommandTransportError("SESSION_CHANGED", "Your session changed. Sign in again.");
    if (this.socket?.readyState === WebSocket.OPEN && this.ready) return;
    if (this.connecting) return this.connecting;
    this.connecting = this.open(endpoint).finally(() => { this.connecting = null; });
    return this.connecting;
  }
  async open(endpoint) {
    const ticketStartedAt = performance.now();
    const ticket = await request("/insights/account/socket-tickets", { scope: this.scope })
      .finally(() => recordSlowStage("SOCKET_TICKET", ticketStartedAt));
    const url = new URL(ticket.endpoint || endpoint);
    if (url.protocol !== "wss:" || url.username || url.password || url.search || url.hash || ticket.expiresAt <= Date.now()) {
      throw new CommandTransportError("INVALID_ENDPOINT", "The secure command connection could not be verified.");
    }
    if (this.closed) throw new CommandTransportError("SESSION_CHANGED", "Your session changed. Sign in again.");
    this.endpoint = url.href; this.ready = false;
    const startedAt = performance.now();
    await new Promise((resolve, reject) => {
      const socket = new WebSocket(url.href, PROTOCOL); this.socket = socket;
      const timeout = setTimeout(() => { socket.close(); reject(new CommandTransportError("COMMAND_OFFLINE", "App backend is offline or its secure connection is unavailable. No new command was sent.")); }, 8_000);
      socket.onopen = () => { if (!this.closed && this.socket === socket) socket.send(JSON.stringify({ protocol: PROTOCOL, kind: "auth", ticket: ticket.ticket })); else socket.close(); };
      socket.onmessage = event => {
        if (this.closed || this.socket !== socket) return;
        if (typeof event.data !== "string" || event.data.length > 1024 * 1024) { socket.close(1009); return; }
        let value; try { value = JSON.parse(event.data); } catch (_) { socket.close(1002); return; }
        if (value.protocol !== PROTOCOL) { socket.close(1002); return; }
        this.lastMessageAt = Date.now();
        if (value.kind === "ping") { socket.send(JSON.stringify({ protocol: PROTOCOL, kind: "pong" })); return; }
        if (value.kind === "ready") {
          this.ready = true; this.attempts = 0; clearTimeout(timeout); resolve();
          clearInterval(this.heartbeat);
          this.heartbeat = setInterval(() => {
            if (Date.now() - this.lastMessageAt > 35_000) {
              clearInterval(this.heartbeat); this.ready = false;
              if (this.socket === socket) this.socket = null;
              socket.close(); this.scheduleRecovery(); return;
            }
            if (socket.readyState === WebSocket.OPEN) socket.send(JSON.stringify({ protocol: PROTOCOL, kind: "ping" }));
          }, 10_000);
          // Recovery only asks about original IDs. Payloads are never retained or resent.
          this.pending.forEach(entry => socket.send(JSON.stringify({ protocol: PROTOCOL, kind: "status", commandId: entry.id })));
        }
        if (value.kind === "response" && TERMINAL.has(value.state)) {
          const entry = this.pending.get(value.commandId);
          if (entry) {
            if (value.state === "ACTION_REQUIRED") {
              entry.resolve(value); entry.actionRequired = true;
              return;
            }
            clearTimeout(entry.timer); this.pending.delete(value.commandId);
            if (!["OUTCOME_UNKNOWN", "NOT_FOUND"].includes(value.state)) rememberId(this.storageKey, value.commandId, true);
            if (entry.actionRequired || entry.recovering) window.dispatchEvent(new CustomEvent("cryptember-command-result", { detail: {
              commandId: value.commandId, scope: this.scope, state: value.state, code: value.code, accepted: value.accepted, message: commandResultMessage(value),
              recovered: entry.recovering === true,
            } }));
            entry.resolve(value);
          }
        }
      };
      socket.onerror = () => { /* Close handles recovery without submitting anything. */ };
      socket.onclose = () => {
        if (this.socket !== socket) return;
        clearTimeout(timeout); clearInterval(this.heartbeat); this.ready = false;
        reject(new CommandTransportError("COMMAND_OFFLINE", "App backend is offline or its secure connection is unavailable. No new command was sent."));
        this.scheduleRecovery();
      };
    }).finally(() => recordSlowStage("CONNECT_AND_AUTH", startedAt));
  }
  scheduleRecovery() {
    clearTimeout(this.retry);
    if (!this.closed && (this.pending.size || this.scope === "USER")) this.retry = setTimeout(() => this.connect(this.endpoint).catch(() => this.scheduleRecovery()),
      Math.min(30_000, 1_000 * 2 ** Math.min(this.attempts++, 5)) + Math.random() * 500);
  }
  query(id) {
    return new Promise(resolve => {
      const timer = setTimeout(() => { this.pending.delete(id); resolve(uncertain(id)); }, 126_000);
      this.pending.set(id, { id, resolve, timer });
      if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(JSON.stringify({ protocol: PROTOCOL, kind: "status", commandId: id }));
    });
  }
  async send(command, endpoint) {
    await this.connect(endpoint);
    if (command.expiresAt <= Date.now()) throw new CommandTransportError("COMMAND_EXPIRED", "The command expired before it could be sent. Review it before submitting again.", command.commandId);
    if (this.pending.size >= 16) throw new CommandTransportError("TOO_MANY_COMMANDS", "Wait for the current commands to finish.");
    if (this.pending.has(command.commandId) || readIds(this.storageKey).includes(command.commandId)) throw new CommandTransportError("COMMAND_ALREADY_SENT", "This command was already sent. Check its original outcome before submitting another command.", command.commandId);
    const startedAt = performance.now();
    return new Promise(resolve => {
      const id = command.commandId;
      const timer = setTimeout(() => {
        const pending = this.pending.get(id);
        if (pending?.actionRequired) window.dispatchEvent(new CustomEvent("cryptember-command-result", { detail: {
          commandId: id, state: "OUTCOME_UNKNOWN", accepted: false,
          message: "The original command deadline has passed. Check activity and positions before submitting a new command.",
        } }));
        resolve(uncertain(id));
        if (pending) {
          // The caller stops waiting, but bounded recovery keeps only the ID and
          // asks for its receipt. A delayed close must never discard this check.
          pending.recovering = true;
          pending.timer = setTimeout(() => this.pending.delete(id), 126_000);
          if (this.socket?.readyState === WebSocket.OPEN && this.ready) {
            try { this.socket.send(JSON.stringify({ protocol: PROTOCOL, kind: "status", commandId: id })); }
            catch (_) { this.scheduleRecovery(); }
          } else this.scheduleRecovery();
        }
      }, Math.max(1, command.expiresAt - Date.now()) + 6_000);
      this.pending.set(id, { id, resolve, timer });
      rememberId(this.storageKey, id);
      try { this.socket.send(JSON.stringify({ ...command, protocol: PROTOCOL, kind: "command" })); }
      catch (_) { this.scheduleRecovery(); }
    }).finally(() => recordSlowStage("COMMAND_RESPONSE", startedAt));
  }
}

export async function routeJavaCommand(input, { scope = "USER", legacy, mode } = {}) {
  const createdAt = Date.now();
  if (!session?.csrfToken) throw new CommandTransportError("AUTHENTICATION_REQUIRED", "Sign in to send a command.", null, 401);
  const startedGeneration = generation;
  const transport = mode || await commandTransportMode();
  if (generation !== startedGeneration) throw new CommandTransportError("SESSION_CHANGED", "Your session changed. Sign in again.");
  if (transport.mode === "draining") throw new CommandTransportError("TRANSPORT_DRAINING", "Commands are paused while the secure connection is being enabled. Wait and refresh.");
  const command = { ...input, commandId: input.commandId || crypto.randomUUID(), createdAt,
    expiresAt: Math.min(input.expiresAt || Infinity, createdAt + (input.type === "TRADE_COMMAND" ? 30_000 : 120_000)) };
  let result;
  if (transport.mode === "legacy") {
    if (!legacy) throw new CommandTransportError("UPGRADE_REQUIRED", "Refresh the page to use the configured command transport.");
    const legacyCommand = { ...command }; delete legacyCommand.createdAt;
    try { result = await legacy(legacyCommand); }
    catch (error) {
      if (!error.status || error.status >= 500) result = envelope(uncertain(command.commandId));
      else throw error;
    }
  } else {
    if (!clients.has(scope)) clients.set(scope, new CommandSocket(scope));
    result = envelope(await clients.get(scope).send(command, transport.endpoint));
  }
  if (generation !== startedGeneration) throw new CommandTransportError("SESSION_CHANGED", "Your session changed. Check command status after signing in again.", command.commandId);
  window.dispatchEvent(new CustomEvent("cryptember-command-result", { detail: { commandId: command.commandId, scope, type: input.type,
    state: result.acknowledgement?.state, code: result.acknowledgement?.code, expiresAt: command.expiresAt,
    accepted: result.acknowledgement?.accepted, message: commandResultMessage(result) } }));
  return result;
}

export async function waitForLegacyCommand(initial, readStatus, expiresAt) {
  let result = initial;
  for (let attempt = 0; result.status !== "ACKNOWLEDGED" && attempt < 10 && Date.now() < expiresAt; attempt += 1) {
    await new Promise(resolve => setTimeout(resolve, Math.min(3_000, 500 + attempt * 250)));
    try { result = await readStatus(); } catch (_) { return envelope(uncertain(initial.commandId)); }
  }
  return result.status === "ACKNOWLEDGED" ? result : envelope(uncertain(initial.commandId));
}
